import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RequireSystemAdmin } from "@/components/auth/RequireSystemAdmin";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { LoadingScreen } from "@/components/common/LoadingScreen";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

// Auth pages (lazy loaded)
const LoginPage = lazy(() =>
  import("@/pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import("@/pages/auth/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/auth/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const AcceptInvitePage = lazy(() =>
  import("@/pages/auth/AcceptInvitePage").then((m) => ({
    default: m.AcceptInvitePage,
  })),
);

// Setup wizard (lazy loaded)
const SetupWizard = lazy(() =>
  import("@/pages/setup/SetupWizard").then((m) => ({
    default: m.SetupWizard,
  })),
);

// Dashboard pages (lazy loaded)
const InboxPage = lazy(() =>
  import("@/pages/inbox/InboxPage").then((m) => ({ default: m.InboxPage })),
);
const CustomersPage = lazy(() =>
  import("@/pages/customers/CustomersPage").then((m) => ({
    default: m.CustomersPage,
  })),
);
const CustomerDetail = lazy(() =>
  import("@/pages/customers/CustomerDetail").then((m) => ({
    default: m.CustomerDetail,
  })),
);
const KnowledgeBasePage = lazy(() =>
  import("@/pages/kb/KnowledgeBasePage").then((m) => ({
    default: m.KnowledgeBasePage,
  })),
);
const AnalyticsPage = lazy(() =>
  import("@/pages/analytics/AnalyticsPage").then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const GeneralSettings = lazy(() =>
  import("@/pages/settings/GeneralSettings").then((m) => ({
    default: m.GeneralSettings,
  })),
);
const TeamSettings = lazy(() =>
  import("@/pages/settings/TeamSettings").then((m) => ({
    default: m.TeamSettings,
  })),
);
const PropertiesSettings = lazy(() =>
  import("@/pages/settings/PropertiesSettings").then((m) => ({
    default: m.PropertiesSettings,
  })),
);
const WidgetsSettings = lazy(() =>
  import("@/pages/settings/WidgetsSettings").then((m) => ({
    default: m.WidgetsSettings,
  })),
);
const WidgetEditPage = lazy(() =>
  import("@/pages/settings/WidgetEditPage").then((m) => ({
    default: m.WidgetEditPage,
  })),
);
const IntegrationsSettings = lazy(() =>
  import("@/pages/settings/IntegrationsSettings").then((m) => ({
    default: m.IntegrationsSettings,
  })),
);
const CannedResponsesSettings = lazy(() =>
  import("@/pages/settings/CannedResponsesSettings").then((m) => ({
    default: m.CannedResponsesSettings,
  })),
);
const TagsSettings = lazy(() =>
  import("@/pages/settings/TagsSettings").then((m) => ({
    default: m.TagsSettings,
  })),
);
const ProfileSettings = lazy(() =>
  import("@/pages/settings/ProfileSettings").then((m) => ({
    default: m.ProfileSettings,
  })),
);
const ApiKeysSettings = lazy(() =>
  import("@/pages/settings/ApiKeysSettings").then((m) => ({
    default: m.ApiKeysSettings,
  })),
);

// Platform admin pages (lazy loaded, system admins only)
const AdminPage = lazy(() =>
  import("@/pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const AdminCompaniesPage = lazy(() =>
  import("@/pages/admin/AdminCompaniesPage").then((m) => ({
    default: m.AdminCompaniesPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import("@/pages/admin/AdminUsersPage").then((m) => ({
    default: m.AdminUsersPage,
  })),
);

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          {/* verify-email is handled by the API (GET redirect), but include a route for completeness */}
          <Route path="/verify-email" element={<Navigate to="/login" replace />} />

          {/* Setup wizard (auth required, no dashboard layout) */}
          <Route
            path="/setup"
            element={
              <ProtectedRoute>
                <SetupWizard />
              </ProtectedRoute>
            }
          />

          {/* Protected routes (auth required) */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/inbox" replace />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:conversationId" element={<InboxPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:customerId" element={<CustomerDetail />} />
            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route
              path="/knowledge-base/articles/:articleId"
              element={<KnowledgeBasePage />}
            />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings" element={<SettingsPage />}>
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route path="team" element={<TeamSettings />} />
              <Route path="properties" element={<PropertiesSettings />} />
              <Route path="widgets" element={<WidgetsSettings />} />
              <Route path="widgets/:widgetId" element={<WidgetEditPage />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="api-keys" element={<ApiKeysSettings />} />
              <Route path="canned-responses" element={<CannedResponsesSettings />} />
              <Route path="tags" element={<TagsSettings />} />
            </Route>
            <Route
              path="/admin"
              element={
                <RequireSystemAdmin>
                  <AdminPage />
                </RequireSystemAdmin>
              }
            >
              <Route index element={<Navigate to="/admin/companies" replace />} />
              <Route path="companies" element={<AdminCompaniesPage />} />
              <Route path="users" element={<AdminUsersPage />} />
            </Route>
          </Route>

          {/* Catch-all: redirect to inbox */}
          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
